import { valibotResolver } from "@hookform/resolvers/valibot";
import { PencilSimpleLine, Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import TextArea from "common/components/ui/TextArea";
import useLeadNotes from "common/datahooks/useLeadNotes";
import { formatLastUpdateAt } from "common/helpers/utils";
import { NoteSchema, type NoteSchemaType } from "common/schemas";
import type { Note as NoteType } from "common/types";

import type React from "react";

const maxVisibleCharacters = 200;
export default function Note({
  note,
  profileId,
  onDelete,
}: {
  profileId: string;
  note: NoteType;
  onDelete: () => void;
}) {
  const { id, text, edited_at: editedAt, created_at: createdAt } = note;
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(
    text.length < maxVisibleCharacters,
  );

  const { updateLeadNote, isUpdatingLeadNote } = useLeadNotes();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NoteSchemaType>({
    defaultValues: { text },
    resolver: valibotResolver(NoteSchema),
  });

  async function onSubmit({ text: newText }: NoteSchemaType) {
    if (newText.trim() === text.trim()) {
      setIsEditing(false);
    } else {
      updateLeadNote({ profileId, noteId: id, newText })
        .then(() => {
          reset({ text: newText });
          setIsEditing(false);
          setIsExpanded(text.length < maxVisibleCharacters);
        })
        .catch(() => {
          toast.error("Error saving note");
        });
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSubmit(onSubmit)();
    }
  }

  if (isEditing) {
    return (
      <div className="flex flex-col gap-y-2">
        <FormField label="" htmlFor="" error={errors.text?.message}>
          <TextArea
            {...register("text")}
            aria-label="Lead notes"
            hasError={!!errors.text?.message}
            autoFocus
            minRows={4}
            maxRows={6}
            placeholder="Write something..."
            onKeyDown={onKeyDown}
            disabled={isUpdatingLeadNote}
          />
        </FormField>

        <div className="flex justify-end gap-x-2">
          <Button
            variant="outline-black"
            disabled={isUpdatingLeadNote}
            onClick={() => setIsEditing(false)}
            size="sm"
          >
            Discard
          </Button>

          <Button
            variant="secondary-purple"
            isLoading={isUpdatingLeadNote}
            onClick={handleSubmit(onSubmit)}
            size="sm"
          >
            Save
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-y-2">
      <div className="relative group">
        <div className="flex flex-1 min-h-16 flex-col overflow-hidden rounded-2xl bg-black-100 p-2.5 text-xs-medium group-hover:bg-black-200">
          <p className="wrap-break-word">
            {isExpanded ? text : `${text.slice(0, maxVisibleCharacters)}...`}
          </p>
          <RenderIf condition={!isExpanded}>
            <button
              type="button"
              className="ml-auto mr-2.5 text-xs text-purple-600"
              onClick={() => setIsExpanded(true)}
            >
              {`Read more >`}
            </button>
          </RenderIf>
        </div>
        <div className="flex p-2.5 gap-x-1.5 opacity-0 group-hover:opacity-100 rounded-2xl top-2.5 right-2.5 bg-whiteGray absolute">
          <Button
            aria-label="edit note"
            onClick={() => setIsEditing(true)}
            intent="iconOnly"
            variant="ghost-black"
            size="xs"
          >
            <PencilSimpleLine />
          </Button>
          <Button
            intent="iconOnly"
            variant="ghost-black"
            size="xs"
            aria-label="delete note"
            onClick={onDelete}
          >
            <Trash />
          </Button>
        </div>
      </div>

      <span className="ml-auto text-xs text-black-500">
        Created {formatLastUpdateAt(createdAt)}{" "}
        <span>{editedAt && `(Edited ${formatLastUpdateAt(editedAt)})`}</span>
      </span>
    </div>
  );
}
